import React, { useState } from 'react';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { stringify as queryStringify } from 'query-string';
import { Spinner } from 'admin-components/components';
import { filter, props, sum } from 'ramda';

import { historyShape, userShape, transactionSummariesShape, transactionsShape, totalNumberOfTransactionsShape } from 'constants/propTypes';

import { Printer } from 'react-feather';

import { Page, Row, Column } from 'components/shared/Layout';

import HideWhenPrinted from 'components/shared/HideWhenPrinted';
import TransactionSummary from './TransactionSummary';
import TransactionDetails from './TransactionDetails';
import UserSearchForm from './UserSearchForm';
import {
  InfoMessage,
  StyledDate,
  StyledLinkButton,
  StyledButtonGroup,
  StyledCalendar,
  StyledCalendarIcon,
  StyledCalendars,
  StyledLabel
} from './Shifts.styles';

const DateButtonTypes = Object.freeze({
  YESTERDAY: 'YESTERDAY',
  TODAY: 'TODAY',
  CALENDAR: 'CALENDAR',
  PERIOD: 'PERIOD'
});

/**
 * @param {string} startDate
 * @returns {string}
 */
const getDateButtonTypeByStartDate = ({ startDate }) => {
  const isToday = moment().isSame(startDate, 'day');
  const isYesterday = !isToday && moment().subtract(1, 'day').isSame(startDate, 'day');

  if (isToday) {
    return DateButtonTypes.TODAY;
  }
  if (isYesterday) {
    return DateButtonTypes.YESTERDAY;
  }

  return DateButtonTypes.CALENDAR;
};

/**
 * @param {Object} history
 * @param {moment.Moment} date
 */
const gotoDate = ({ history, date = null }) => {
  if (!date) {
    history.push('/shifts');
  } else {
    const dateStr = date.format('YYYY-MM-DD');

    const queryString = queryStringify({
      startDate: dateStr,
      endDate: dateStr
    });

    history.push(`/shifts?${queryString}`);
  }
};

const Shifts = ({
  shiftReports: { transactionSummaries, transactions, totalNumberOfTransactions, freeTransactions },
  user: { offices },
  startDate,
  history,
  loading,
  username,
  officeCode,
  setUserDetails
}) => {
  const startDateMoment = moment(startDate);
  const dateButtonTypeByStartDate = getDateButtonTypeByStartDate({ startDate });
  const [selectedDateButtonType, setSelectedDateButtonType] = useState(dateButtonTypeByStartDate);
  const [fromDatePeriod, setFromDatePeriod] = useState(moment());
  const calendarVisible = selectedDateButtonType === DateButtonTypes.CALENDAR;
  const calendarsVisible = selectedDateButtonType === DateButtonTypes.PERIOD;
  const transactionSummaryVisible =
    transactionSummaries &&
    transactionSummaries.length > 0 &&
    sum(filter(Boolean)(props(['EUR', 'GBP', 'USD'], totalNumberOfTransactions))) > 0;
  const transactionsVisible = transactions && transactions.length > 0;
  const freeTransactionsVisible = freeTransactions && freeTransactions.length > 0;

  const onDateButtonClick = (type) => {
    if (type === DateButtonTypes.TODAY) {
      gotoDate({ history });
    } else if (type === DateButtonTypes.YESTERDAY) {
      const yesterday = moment().subtract(1, 'day');
      gotoDate({ history, date: yesterday });
    }

    setSelectedDateButtonType(type);
  };

  const onCalendarDateChange = (date) => {
    gotoDate({ history, date: moment(date) });
  };

  const onCalendarDateChangeStartDate = (date) => setFromDatePeriod(moment(date));
  const onCalendarDateChangeEndDate = (date, fromDateCalendar) => {
    const queryString = queryStringify({
      startDate: moment(fromDateCalendar._i).subtract(1, 'day').format('YYYY-MM-DD'),
      endDate: moment(date).format('YYYY-MM-DD'),
      shouldUseByPeriod: true
    });

    history.push(`/shifts?${queryString}`);
  };

  return (
    <Page>
      <Row span={14} start={2}>
        <Row alignItems="center">
          <Column span={6}>
            <h1>
              <FormattedMessage id="shifts.shiftBalance" />
            </h1>
          </Column>
          <Column span={10} justifyContent="flex-end">
            <HideWhenPrinted>
              <StyledLinkButton onClick={window.print}>
                <Printer />
                <FormattedMessage id="shifts.print" />
              </StyledLinkButton>
            </HideWhenPrinted>
          </Column>
        </Row>

        <UserSearchForm offices={offices} username={username} officeCode={officeCode} setUserDetails={setUserDetails} />

        <StyledButtonGroup
          onClick={onDateButtonClick}
          selectedValue={selectedDateButtonType}
          data-cy="date-buttons"
          buttons={[
            {
              value: DateButtonTypes.TODAY,
              label: <FormattedMessage id="shifts.today" />
            },
            {
              value: DateButtonTypes.YESTERDAY,
              label: <FormattedMessage id="shifts.yesterday" />
            },
            {
              value: DateButtonTypes.CALENDAR,
              label: (
                <>
                  <StyledCalendarIcon size={20} /> <FormattedMessage id="shifts.calendar" />
                </>
              )
            },
            {
              value: DateButtonTypes.PERIOD,
              label: (
                <>
                  <StyledCalendarIcon size={20} /> <FormattedMessage id="shifts.periodOfTime" />
                </>
              )
            }
          ]}
        />

        {!calendarVisible && !calendarsVisible && <StyledDate>{startDateMoment.format('DD MMMM YYYY')}</StyledDate>}

        {calendarVisible && (
          <StyledCalendar
            id="calendar--0"
            showNeighboringMonth={false}
            tabIndex="-1"
            value={startDateMoment.toDate()}
            onChange={onCalendarDateChange}
            maxDate={new Date()}
          />
        )}

        {!loading && calendarsVisible && (
          <StyledCalendars>
            <div>
              <StyledLabel>
                <FormattedMessage id="clubEurostar.benefits.startDate" />:
              </StyledLabel>
              <StyledCalendar
                id="calendar--1"
                showNeighboringMonth={true}
                tabIndex="-1"
                value={fromDatePeriod.toDate()}
                onChange={onCalendarDateChangeStartDate}
                maxDate={new Date()}
              />
            </div>
            <div>
              <StyledLabel>
                <FormattedMessage id="clubEurostar.benefits.endDate" />:
              </StyledLabel>
              <StyledCalendar
                id="calendar--2"
                showNeighboringMonth={true}
                tabIndex="-1"
                value={moment().add(6, 'day').toDate()}
                onChange={(date) => onCalendarDateChangeEndDate(date, fromDatePeriod)}
                minDate={fromDatePeriod.toDate()}
                maxDate={fromDatePeriod.add(6, 'day').toDate()}
              />
            </div>
          </StyledCalendars>
        )}

        {!loading && transactionSummaryVisible && (
          <>
            <h3>
              <FormattedMessage id="shifts.transactionSummary" />
            </h3>
            <TransactionSummary transactionSummaries={transactionSummaries} totalNumberOfTransactions={totalNumberOfTransactions} />
          </>
        )}

        {loading && (
          <Column alignItems="center" justifyContent="center">
            <Spinner />
          </Column>
        )}

        {!loading && transactionsVisible && (
          <HideWhenPrinted>
            <h3>
              <FormattedMessage id="shifts.transactionDetails" />
            </h3>
            <TransactionDetails transactions={transactions} />
          </HideWhenPrinted>
        )}

        {!loading && freeTransactionsVisible && (
          <HideWhenPrinted>
            <h4>
              <FormattedMessage id="shifts.freeTransactionDetails" />
            </h4>
            <TransactionDetails transactions={freeTransactions} isFreeTransaction />
          </HideWhenPrinted>
        )}

        {!loading && !transactionsVisible && !freeTransactionsVisible && (
          <InfoMessage>
            <FormattedMessage id="shifts.reportNotGenerated" />
          </InfoMessage>
        )}
      </Row>
    </Page>
  );
};

Shifts.propTypes = {
  user: userShape,
  shiftReports: PropTypes.shape({
    transactionSummaries: transactionSummariesShape,
    transactions: transactionsShape,
    freeTransactions: transactionsShape,
    totalNumberOfTransactions: totalNumberOfTransactionsShape
  }),
  startDate: PropTypes.string.isRequired,
  history: historyShape,
  loading: PropTypes.bool,
  username: PropTypes.string,
  officeCode: PropTypes.string,
  setUserDetails: PropTypes.func
};

export default withRouter(Shifts);
